import { useState, useEffect } from 'react';
import { MdOutlineRefresh, MdOutlineOpenInFull, MdOutlineMoreVert } from 'react-icons/md';
import { LineChartCard, DonutChartCard } from '@/components/charts';
import CaseViewModal, { type CaseRow } from '@/components/CaseViewModal';
import StatsCard from '@/components/StatsCard';
import StatusBadge from '@/components/StatusBadge';
import ViewButton from '@/components/ViewButton';
import { DataTable } from '@podus/ui';
import type { Column } from '@podus/ui';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import { useYear } from '@/context/YearContext';
import type { AdminDashboardStats, ChartDataPoint, Case } from '@/types';

/* ─── Types ─── */

interface ResolutionStats {
    avgResolutionTime: number;
    resolutionRate: number;
    resolvedCases: number;
    totalCases: number;
}

interface DonutPoint {
    name: string;
    value: number;
    color: string;
}

/* ─── Helpers ─── */

function mapToRow(c: Case, index: number): CaseRow {
    const applicant = c.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
    return {
        id: c.id,
        sn: index + 1,
        date: new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        complainantName: applicant?.name ?? c.caseNumber,
        caseCategory: c.caseCategory,
        disputeCategory: c.disputeCategory,
        evidence: c.evidence?.length ?? 'Nil',
        amountPaid: `₦${Number(c.amountPaid).toLocaleString()}`,
        status: c.status,
    };
}

const formatCurrency = (value: number) => `₦${(value / 1000).toFixed(0)}K`;

export default function Dashboard() {
    const { yearQuery } = useYear();

    const [selectedCase, setSelectedCase] = useState<CaseRow | null>(null);
    const [dashStats, setDashStats] = useState<AdminDashboardStats | null>(null);
    const [resolutionStats, setResolutionStats] = useState<ResolutionStats | null>(null);
    const [financialChart, setFinancialChart] = useState<ChartDataPoint[]>([]);
    const [caseVolumeChart, setCaseVolumeChart] = useState<ChartDataPoint[]>([]);
    const [statusDist, setStatusDist] = useState<DonutPoint[]>([]);
    const [caseCategoryDist, setCaseCategoryDist] = useState<DonutPoint[]>([]);
    const [disputeCategoryDist, setDisputeCategoryDist] = useState<DonutPoint[]>([]);
    const [recentCases, setRecentCases] = useState<CaseRow[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get<Case[]>('/api/cases/recent')
            .then((res) => setRecentCases((res.response.data ?? []).map(mapToRow)))
            .catch(() => setRecentCases([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        Promise.all([
            api.get<AdminDashboardStats>(`/api/dashboard/admin${yearQuery}`),
            api.get<ResolutionStats>(`/api/analytics/resolution${yearQuery}`),
            api.get<ChartDataPoint[]>(`/api/dashboard/admin/financial-chart${yearQuery}`),
            api.get<ChartDataPoint[]>(`/api/dashboard/admin/case-volume-chart${yearQuery}`),
            api.get<DonutPoint[]>(`/api/analytics/case-status-distribution${yearQuery}`),
            api.get<DonutPoint[]>(`/api/analytics/case-category-distribution${yearQuery}`),
            api.get<DonutPoint[]>(`/api/analytics/dispute-category-distribution${yearQuery}`),
        ])
            .then(([statsRes, resolutionRes, financialRes, volumeRes, statusRes, caseCatRes, disputeCatRes]) => {
                setDashStats(statsRes.response.data ?? null);
                setResolutionStats(resolutionRes.response.data ?? null);
                setFinancialChart(financialRes.response.data ?? []);
                setCaseVolumeChart(volumeRes.response.data ?? []);
                setStatusDist(statusRes.response.data ?? []);
                setCaseCategoryDist(caseCatRes.response.data ?? []);
                setDisputeCategoryDist(disputeCatRes.response.data ?? []);
            })
            .catch(() => {
                setDashStats(null);
                setResolutionStats(null);
                setFinancialChart([]);
                setCaseVolumeChart([]);
                setStatusDist([]);
                setCaseCategoryDist([]);
                setDisputeCategoryDist([]);
            });
    }, [yearQuery]);

    const statsCards = [
        { label: 'Users', value: String(dashStats?.users ?? 0) },
        { label: 'Cases', value: String(dashStats?.cases ?? 0) },
        { label: 'Gross Revenue', value: `₦${Number(dashStats?.grossRevenue ?? 0).toLocaleString()}` },
        { label: 'Avg Resolution Time', value: `${resolutionStats?.avgResolutionTime ?? 0} hrs` },
        { label: 'Resolution Rate', value: `${resolutionStats?.resolutionRate ?? 0}%` },
    ];

    const CASE_COLUMNS: Column<CaseRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'date', label: 'Date' },
        { key: 'complainantName', label: 'Complainant Name' },
        { key: 'caseCategory', label: 'Case Category' },
        { key: 'disputeCategory', label: 'Dispute Category' },
        { key: 'evidence', label: 'No of Evidence' },
        { key: 'amountPaid', label: 'Amount Paid' },
        {
            key: 'status',
            label: 'Case Status',
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => setSelectedCase(row)} />,
        },
    ];

    return (
        <div className="space-y-6  pt-10 pb-6">
            <div className="px-4 lg:px-8 mb-12.5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-12.5">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-secondary sm:text-3xl">MultiDoor Admin Dashboard</h1>
                            <span className="rounded-full bg-[#FFA800] px-3 py-0.5 text-[12px] font-medium text-[#4F4F4F]">
                                Version 1.0
                            </span>
                        </div>
                        <p className="mt-2 text-[14px] text-[#333333]">
                            Last updated:{' '}
                            {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                        {statsCards.map((s) => (
                            <StatsCard key={s.label} stat={s} />
                        ))}
                    </div>
                </div>

                <div className="grid gap-5 lg:grid-cols-2">
                    <LineChartCard
                        title="Financial Overview"
                        data={financialChart as Record<string, unknown>[]}
                        series={[{ dataKey: 'grossRevenue', stroke: '#ff6600', name: 'Gross Revenue' }]}
                        yTickFormatter={formatCurrency}
                        tooltipFormatter={(v) => `₦${Number(v).toLocaleString()}`}
                        // legendBoxes={[
                        //     {
                        //         label: 'Gross Revenue',
                        //         value: `₦${Number(dashStats?.grossRevenue ?? 0).toLocaleString()}`,
                        //         dotColor: '#ff6600',
                        //         textColor: '#FF6600',
                        //         bgColor: '#FFF4EC',
                        //         borderColor: '#fed7aa',
                        //     },
                        // ]}
                    />
                    <DonutChartCard
                        title="Case Status"
                        subtitle="Snapshot of the status of all cases"
                        data={statusDist}
                        innerRadius={85}
                        outerRadius={135}
                        height={300}
                    />
                </div>

                <div className="mt-5 grid gap-5 lg:grid-cols-2">
                    <CaseCategoriesCard caseCategoryData={caseCategoryDist} disputeCategoryData={disputeCategoryDist} />
                    <LineChartCard
                        title="Case Trend Volume"
                        data={caseVolumeChart as Record<string, unknown>[]}
                        series={[
                            { dataKey: 'newCases', stroke: '#16a34a', name: 'New Cases' },
                            { dataKey: 'pending', stroke: '#7c3aed', name: 'Pending' },
                            { dataKey: 'resolved', stroke: '#ff6600', name: 'Resolved' },
                        ]}
                    />
                </div>
            </div>

            <DataTable
                title="Recent Cases"
                columns={CASE_COLUMNS}
                data={recentCases}
                getRowKey={(row) => row.id}
                onExportCSV={() => exportCSV('recent-cases', CASE_COLUMNS, recentCases)}
                searchPlaceholder="Search Complainant Name"
            />

            <CaseViewModal open={!!selectedCase} onClose={() => setSelectedCase(null)} caseData={selectedCase} />
        </div>
    );
}

/* ─── Case Categories Card (with tabs) ─── */

interface CaseCategoriesCardProps {
    caseCategoryData: DonutPoint[];
    disputeCategoryData: DonutPoint[];
    year?: number;
    onYearChange?: (year: number) => void;
}

function CaseCategoriesCard({ caseCategoryData, disputeCategoryData, year, onYearChange }: CaseCategoriesCardProps) {
    const [activeTab, setActiveTab] = useState<'case' | 'dispute'>('case');
    const data = activeTab === 'case' ? caseCategoryData : disputeCategoryData;
    const tabTitle = activeTab === 'case' ? 'Case Categories' : 'Dispute Categories';
    const tabSubtitle =
        activeTab === 'case' ? 'Snapshot of the categories of all cases' : 'Snapshot of the dispute categories';

    const header = (
        <div className="flex items-center gap-2">
            {/* <MdOutlineRefresh size={18} className="cursor-pointer text-[#999] hover:text-secondary" /> */}
            <div className="flex gap-4 text-[15px]">
                <button
                    type="button"
                    onClick={() => setActiveTab('case')}
                    className={`font-semibold transition-colors ${activeTab === 'case' ? 'text-secondary' : 'text-[#ccc] hover:text-[#999]'}`}
                >
                    Case Categories
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('dispute')}
                    className={`font-semibold transition-colors ${activeTab === 'dispute' ? 'text-secondary' : 'text-[#ccc] hover:text-[#999]'}`}
                >
                    Dispute Categories
                </button>
            </div>
        </div>
    );

    return (
        <DonutChartCard
            title={tabTitle}
            subtitle={tabSubtitle}
            data={data}
            header={header}
            innerRadius={85}
            outerRadius={135}
            height={300}
            year={year}
            onYearChange={onYearChange}
        />
    );
}
